/**
 * useOfferRingtone — driver-offer ringtone.
 *
 * 2026-06-01 (iter-63): switched from Web Audio synthesis to a
 * runtime-generated WAV blob driven through an HTMLAudioElement.
 *
 * Why the switch?
 *   The previous Web-Audio implementation worked on desktop but was
 *   silent on real mobile phones (iOS Safari + Android Chrome).
 *   Reasons:
 *     • iOS Safari silently muted Web-Audio oscillators when the
 *       phone's silent/ring switch was set, even with an unlocked
 *       AudioContext, AND sometimes drops audio output entirely after
 *       a tab loses then regains focus.
 *     • Android Chrome's autoplay policy doesn't always honour a
 *       previously-resumed AudioContext when notes are scheduled
 *       from setInterval rather than from a gesture handler.
 *     • Some mobile browsers route Web-Audio output to a different
 *       audio channel than HTMLAudioElement, so the user's media
 *       volume slider has no effect on Web-Audio playback.
 *
 *   HTMLAudioElement with a real WAV asset plays through the standard
 *   media channel, respects the media-volume slider, and once unlocked
 *   from a user gesture (the "Enable Sound" tap) can be replayed
 *   reliably from any context including setInterval — which is exactly
 *   the contract we need.
 *
 * Asset strategy:
 *   We don't ship an .mp3/.wav file. The WAV is synthesised once at
 *   mount time as a Blob, the URL is held in a ref, and the same
 *   Audio element replays it. ~12 KB of PCM in memory. Zero network
 *   cost, zero licensing, works fully offline.
 *
 * Contract (UNCHANGED — Driver.jsx does not need updating):
 *   useOfferRingtone({ activeOfferId, muted })
 *     • `activeOfferId` truthy && muted=false → plays the chime
 *       immediately, then every RING_PERIOD_MS while still active.
 *     • Identity change retriggers (null→x, x→y, x→null).
 *     • Flipping muted to true stops the loop within one tick.
 *     • Returns:
 *         - unlock()   → resume audio session from a user gesture.
 *         - testBeep() → also unlock + play a short audible note;
 *                        resolves true if audible playback started.
 *
 * iOS / Android caveats (documented for ops):
 *   • iPhone silent/ring switch: HTMLAudioElement is also subject to
 *     the silent switch when the page is not user-interacted. After a
 *     user gesture has occurred, it normally plays through media
 *     volume regardless of the switch — but old iOS versions vary.
 *     The "Enable Sound" banner explicitly tests this with an audible
 *     beep and asks the user to retry if they didn't hear it.
 *   • Android browser tab "muted by user" overrides everything: the
 *     hook can't detect this state. Toast wording mentions this.
 *   • iOS background tabs: HTMLAudioElement playback is suspended when
 *     the tab goes background. When the tab returns to foreground and
 *     setInterval next fires, .play() resumes audibly. Acceptable for
 *     a ride-offer driver app — driver should be looking at the tab.
 */
import { useCallback, useEffect, useRef } from "react";

const RING_PERIOD_MS = 4000;       // re-chime every 4s while offer is active
const RINGTONE_VOLUME = 0.95;      // HTMLAudioElement volume 0..1

// ---------------------------------------------------------------------------
// WAV synthesis. Produces a two-tone chime (880Hz → 660Hz) as an 8-bit mono
// PCM WAV blob. ~12 KB in memory.
// ---------------------------------------------------------------------------
function buildChimeWavBlob() {
  const sampleRate = 22050;
  const totalSec   = 0.55;
  const numSamples = Math.floor(sampleRate * totalSec);
  const notes = [
    { freq: 880, start: 0.00, dur: 0.18 },
    { freq: 660, start: 0.22, dur: 0.22 },
  ];
  const pcm = new Uint8Array(numSamples);
  for (let i = 0; i < numSamples; i++) {
    const t = i / sampleRate;
    let s = 0;
    for (const n of notes) {
      if (t >= n.start && t < n.start + n.dur) {
        const lt = t - n.start;
        const attack  = 0.02;
        const release = 0.04;
        let env = 1;
        if (lt < attack) env = lt / attack;
        else if (lt > n.dur - release) env = Math.max(0, (n.dur - lt) / release);
        s += Math.sin(2 * Math.PI * n.freq * t) * env * 0.6;
      }
    }
    // -1..+1 → unsigned 8-bit (0..255 centered at 128)
    const v = Math.round(128 + Math.max(-1, Math.min(1, s)) * 127);
    pcm[i] = v < 0 ? 0 : v > 255 ? 255 : v;
  }
  const header = new ArrayBuffer(44);
  const view = new DataView(header);
  const writeString = (offset, str) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };
  const dataSize = pcm.length;
  writeString(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);     // PCM
  view.setUint16(22, 1, true);     // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate, true); // byte rate (mono, 8-bit)
  view.setUint16(32, 1, true);     // block align
  view.setUint16(34, 8, true);     // bits per sample
  writeString(36, "data");
  view.setUint32(40, dataSize, true);
  return new Blob([header, pcm], { type: "audio/wav" });
}

export function useOfferRingtone({ activeOfferId, muted }) {
  const audioElRef     = useRef(null);
  const blobUrlRef     = useRef(null);
  const intervalRef    = useRef(null);
  const playingForRef  = useRef(null);
  const unlockedRef    = useRef(false);

  // Lazy build of the Audio element + blob URL. Safe to call repeatedly.
  // The element is created once and reused for every chime so the
  // browser doesn't need to re-decode the WAV on every replay.
  const ensureAudio = useCallback(() => {
    if (audioElRef.current) return audioElRef.current;
    if (typeof window === "undefined" || typeof Audio === "undefined") return null;
    try {
      const blob = buildChimeWavBlob();
      const url  = URL.createObjectURL(blob);
      blobUrlRef.current = url;
      const el = new Audio(url);
      el.preload = "auto";
      el.volume  = RINGTONE_VOLUME;
      // playsInline on iOS so the element doesn't try to take over
      // the screen (relevant for video, harmless for audio).
      try { el.setAttribute("playsinline", ""); } catch { /* ignore */ }
      audioElRef.current = el;
      return el;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[ringtone] HTMLAudioElement unavailable:", e?.message);
      return null;
    }
  }, []);

  // Public unlock — must run inside a user-gesture handler (click/tap).
  // Plays the chime once muted, immediately stops it, just to satisfy
  // the browser's "first play came from a gesture" requirement. Subsequent
  // .play() calls from setInterval will then succeed.
  const unlock = useCallback(() => {
    const el = ensureAudio();
    if (!el) return;
    if (unlockedRef.current) return;
    const previousVolume = el.volume;
    // Play silently to mark the element as gesture-unlocked. Some iOS
    // versions still require an audible play; testBeep covers that path.
    el.volume = 0;
    const p = el.play();
    if (p && typeof p.then === "function") {
      p.then(() => {
        try { el.pause(); el.currentTime = 0; } catch { /* ignore */ }
        el.volume = previousVolume;
        unlockedRef.current = true;
      }).catch(() => {
        el.volume = previousVolume;
        // Autoplay policy still in effect — testBeep from a real tap
        // will retry with audible volume.
      });
    } else {
      // Old browsers may return undefined from play(). Restore volume.
      el.volume = previousVolume;
    }
  }, [ensureAudio]);

  // Explicit test-beep — called from the "Enable Sound" button tap.
  // Plays the chime audibly so the driver gets immediate confirmation
  // that sound is working. Returns true if play() resolves successfully.
  //
  // Why we play the FULL chime (not a soft chirp) here?
  //   On iOS Safari the chime that fires from setInterval later is the
  //   same Audio element with the same .play() call. By playing the
  //   real chime now from inside the gesture, we prove end-to-end that
  //   the exact audio path that will fire on a real offer is working.
  //   If the user can't hear THIS beep, the offer ringtone won't be
  //   audible either — so the banner correctly stays visible and the
  //   driver can adjust volume / silent switch and retry.
  const testBeep = useCallback(async () => {
    const el = ensureAudio();
    if (!el) return false;
    try {
      el.volume = RINGTONE_VOLUME;
      el.currentTime = 0;
      const p = el.play();
      if (p && typeof p.then === "function") {
        await p;
      }
      unlockedRef.current = true;
      return true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[ringtone] testBeep play() rejected:", e?.message);
      return false;
    }
  }, [ensureAudio]);

  // First-interaction auto-unlock for desktop drivers. On mobile this
  // typically fires from the Playwright/touch sequence but desktop users
  // who never go through Enable Sound (because the banner is mobile-
  // friendly only) still get unlocked on first click.
  useEffect(() => {
    const handler = () => {
      if (unlockedRef.current) return;
      unlock();
    };
    document.addEventListener("pointerdown", handler, true);
    document.addEventListener("keydown", handler, true);
    return () => {
      document.removeEventListener("pointerdown", handler, true);
      document.removeEventListener("keydown", handler, true);
    };
  }, [unlock]);

  // Play one chime via the Audio element. Resets currentTime so back-to-
  // back replays work without re-loading the resource.
  const playChimeOnce = useCallback(() => {
    const el = audioElRef.current;
    if (!el) return;
    try {
      el.volume = RINGTONE_VOLUME;
      el.currentTime = 0;
      const p = el.play();
      if (p && typeof p.then === "function") {
        p.catch((e) => {
          // Play can fail if the tab was just backgrounded or the
          // audio session got revoked. We log once at warn so ops
          // can correlate with a missed ringtone report, but we do
          // NOT throw — the interval will simply retry on the next
          // tick.
          // eslint-disable-next-line no-console
          console.warn("[ringtone] play() rejected mid-loop:", e?.message);
        });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[ringtone] play() threw:", e?.message);
    }
  }, []);

  // Start/stop/restart the repeating loop on (activeOfferId, muted) edges.
  useEffect(() => {
    const wantId = !muted && activeOfferId ? activeOfferId : null;

    // Tear down if id changed OR we no longer want a ring.
    if (playingForRef.current && playingForRef.current !== wantId) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      const el = audioElRef.current;
      if (el) {
        try { el.pause(); el.currentTime = 0; } catch { /* ignore */ }
      }
      playingForRef.current = null;
    }

    if (wantId && playingForRef.current !== wantId) {
      ensureAudio();
      playingForRef.current = wantId;
      // Fire one immediately so the driver hears it the moment the
      // offer card appears, not after a full RING_PERIOD_MS delay.
      playChimeOnce();
      intervalRef.current = setInterval(playChimeOnce, RING_PERIOD_MS);
    }
    return () => {
      if (playingForRef.current === null && intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [activeOfferId, muted, playChimeOnce, ensureAudio]);

  // Unmount cleanup — pause audio, release the blob URL.
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      const el = audioElRef.current;
      audioElRef.current = null;
      playingForRef.current = null;
      if (el) {
        try { el.pause(); el.src = ""; } catch { /* ignore */ }
      }
      if (blobUrlRef.current) {
        try { URL.revokeObjectURL(blobUrlRef.current); } catch { /* ignore */ }
        blobUrlRef.current = null;
      }
    };
  }, []);

  return { unlock, testBeep };
}
